import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Subscription } from 'rxjs';

import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatDialog } from '@angular/material/dialog';

import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { Storage } from '@ionic/storage';

import { TenantService } from '../../services/tenants-service.service';
import { PropertyService } from '../../services/property.service';
import {
  CaptureReadingService,
  MeterReading,
  SyncResult,
} from '../../services/data-service/captures/capture-meter.service';

import { MeterOcrComponent } from './MeterOcr.component';
import { OverConsumtionDialog } from '../warnings/over-consumption';
import { VerifyCaptureDialog } from '../warnings/verify-capture';
import { MeterWorkflowService } from '../../services/meter-workflow.service';

interface MeterUiItem {
  meterId: string;
  meterNo: string;
  captureType?: string;
  readingCaptureType?: string;
  readingList?: any[];
  readingPeriod?: string;
  readingTS?: string | null;
  status?: string;
  totalCharge?: number;
  tradingName?: string;
  unitNo?: string;
  utilityType?: string;
  propertyId?: string;
  errorMessageList?: any;
  unitId?: string;
}

@Component({
  selector: 'app-capture-meter',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatSelectModule,
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    FormsModule,
    RouterModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
    MatInputModule,
    FlexLayoutModule,
    MeterOcrComponent,
    MatExpansionModule,
    MatProgressBar,
  ],
  templateUrl: './capture-meter.component.html',
  styleUrl: './capture-meter.component.scss',
})
export class CaptureMeterComponent implements OnInit, OnDestroy {
  @ViewChildren('fileInput') fileInputs!: QueryList<ElementRef<HTMLInputElement>>;

  form!: FormGroup;
  imageForm!: FormGroup;

  tenant: MeterUiItem | null = null;
  storedMeter: MeterUiItem | null = null;

  meterId = '';
  unitId = '';
  searchType = 'wo-Unit';
  source = 'meter-list';
  datePeriod = '';
  readingTypes: any[] = [
    { name: 'PERIOD_READING', value: 'Period Reading' },
    { name: 'BO_READING', value: 'Beneficial Occupation' },
    { name: 'VACATING_READING', value: 'Vacating' }
  ];

  targetDates: any = null;
  allowCapture = true;
  responseLoaded = false;
  currentDate = new Date();

  uploadProgress = 0;
  currentStep = '';
  isSaving = false;
  isUploadingImages = false;
  showSuccessMessage = false;
  showErrorMessage = false;
  errorMessage = '';
  subMessage = '';

  numberOfImages = 1;

  private uploadProgressSub?: Subscription;

  dialog = inject(MatDialog);

  images = [
    {
      base64: '',
      label: 'Reading',
      placeholder: 'Capture Reading',
      formControl: 'captured',
    },
    {
      base64: '',
      label: 'Phase 2',
      placeholder: 'Capture Reading',
      formControl: 'captured2',
    },
    {
      base64: '',
      label: 'Phase 3',
      placeholder: 'Capture Reading',
      formControl: 'captured3',
    },
    {
      base64: '',
      label: 'KVA',
      placeholder: 'Capture KVA reading',
      formControl: 'captured4',
    },
  ];

  constructor(
    private tenantService: TenantService,
    private route: ActivatedRoute,
    private meterService: CaptureReadingService,
    private propertyService: PropertyService,
    private router: Router,
    private fb: FormBuilder,
    private storage: Storage,
    private workflow: MeterWorkflowService
  ) { }

  ngOnInit(): void {
    this.initForms();
    this.storedMeter = this.getStoredMeter();

    this.route.queryParams.subscribe(async (params) => {
      this.meterId = params['meterId'] || this.storedMeter?.meterId || '';
      this.unitId = params['unitId'] || this.storedMeter?.unitId || '';
      this.searchType = params['searchType'] || 'wo-Unit';
      this.source =
        params['source'] || localStorage.getItem('captureSource') || 'meter-list';

      if (this.storedMeter) {
        this.applyMeterToUi(this.storedMeter);
      }

      await this.loadMeterContext();
    });

    this.uploadProgressSub = this.meterService.uploadProgress$.subscribe(
      (progress) => {
        if (!progress) return;
        this.uploadProgress = progress.percent;
        this.currentStep = `Uploading image ${progress.sequence}...`;
      }
    );
  }

  ngOnDestroy(): void {
    this.uploadProgressSub?.unsubscribe();
  }

  private initForms(): void {
    this.form = this.fb.group({
      tradingName: [''],
      unitNumber: [''],
      meterNumber: [''],
      utilityType: [''],
      isManual: [''],
      captureType: ['PERIOD_READING'],
      status: [''],
      id: [''],
      unitId: [''],
      readingValueList: [],
    });

    this.imageForm = this.fb.group({
      captured: ['', Validators.required],
      captured2: [''],
      captured3: [''],
      captured4: [''],
    });
  }
  private getStoredMeter(): MeterUiItem | null {
    return this.workflow.getStoredMeter<MeterUiItem>('currentMeter');
  }

  private async loadMeterContext(): Promise<void> {
    try {
      const meterId = this.meterId || this.storedMeter?.meterId || '';
      const unitId = this.unitId || this.storedMeter?.unitId || '';

      if (!meterId) {
        this.handleTenantNotFound();
        return;
      }

      const tenants = await this.fetchTenant(unitId, meterId);

      if (!tenants?.length) {
        if (this.storedMeter) {
          this.applyMeterToUi(this.storedMeter);
          this.responseLoaded = true;
          await this.loadTargetDatesAndCheckCapture();
        }
        return;
      }

      const normalizedMeter = this.normalizeTenantToMeterItem(tenants[0], meterId);

      // keep existing local readingPeriod if backend tenant data does not carry it
      normalizedMeter.readingPeriod =
        this.normalizeReadingPeriod(normalizedMeter.readingPeriod) ||
        this.normalizeReadingPeriod(this.storedMeter?.readingPeriod) ||
        this.normalizeReadingPeriod(localStorage.getItem('currentReadingPeriod')) ||
        this.resolveDatePeriod();

      this.tenant = normalizedMeter;
      this.storedMeter = normalizedMeter;
      localStorage.setItem('currentMeter', JSON.stringify(normalizedMeter));
      this.applyMeterToUi(normalizedMeter);

      const matchingMeter =
        tenants[0]?.meterList?.find((m: any) => m.id === meterId) ||
        tenants[0]?.meterList?.[0];

      if (matchingMeter?.readingValueList) {
        this.patchReadingValues(matchingMeter.readingValueList);
      }

      this.responseLoaded = true;
      await this.loadTargetDatesAndCheckCapture();
    } catch (error) {
      console.error('Error fetching tenant:', error);

      if (this.storedMeter) {
        this.applyMeterToUi(this.storedMeter);
        this.responseLoaded = true;
        await this.loadTargetDatesAndCheckCapture();
      }
    }
  }
private normalizeTenantToMeterItem(tenantData: any, meterId: string): MeterUiItem {
  const meter =
    tenantData?.meterList?.find((m: any) => m.id === meterId) ||
    tenantData?.meterList?.[0] ||
    {};

  return {
    meterId,
    meterNo: meter?.meterNumber || '',
    captureType: meter?.captureType || '',
    readingCaptureType: meter?.captureType || '',
    readingList: meter?.readingValueList || [],
    readingPeriod:
      this.normalizeReadingPeriod(tenantData?.readingPeriod) ||
      this.normalizeReadingPeriod(meter?.readingPeriod) ||
      this.normalizeReadingPeriod(this.storedMeter?.readingPeriod) ||
      this.normalizeReadingPeriod(localStorage.getItem('currentReadingPeriod')) ||
      '',
    readingTS: meter?.readingTS || this.storedMeter?.readingTS || null,
    status: meter?.captured || this.storedMeter?.status || 'NOT_CAPTURED',
    totalCharge: this.storedMeter?.totalCharge || 0,
    tradingName:
      tenantData?.tenant?.tradingName || this.storedMeter?.tradingName || '',
    unitNo: tenantData?.unitNumber || this.storedMeter?.unitNo || '',
    utilityType: meter?.utilityType || this.storedMeter?.utilityType || '',
    propertyId: tenantData?.propertyId || this.storedMeter?.propertyId || '',
    errorMessageList: this.storedMeter?.errorMessageList || [],
    unitId: tenantData?.unitId || this.unitId || '',
  };
}
  private applyMeterToUi(meter: MeterUiItem): void {
    this.form.patchValue({
      tradingName: meter?.tradingName || '',
      unitNumber: meter?.unitNo || '',
      meterNumber: meter?.meterNo || '',
      utilityType: meter?.utilityType || '',
      isManual:
        (meter?.readingCaptureType || meter?.captureType || '').toUpperCase() ===
        'MANUAL',
      captureType: 'PERIOD_READING',
      status: meter?.status || 'NOT_CAPTURED',
      id: meter?.meterId || '',
      unitId: meter?.unitId || '',
    });

    this.configureImagesForUtility(meter?.utilityType || '');
  }

  private patchReadingValues(readingValueList: any[]): void {
    this.imageForm.patchValue({
      captured: readingValueList?.[0]?.value || '',
      captured2: readingValueList?.[1]?.value || '',
      captured3: readingValueList?.[2]?.value || '',
      captured4: readingValueList?.[3]?.value || '',
    });
  }

  private async fetchTenant(unitId: string, meterId: string): Promise<any[]> {
    if (this.searchType === 'wo-Unit' || !unitId) {
      return this.tenantService.getTenantByMeterId(meterId);
    }

    return this.tenantService.getTenantById(unitId, meterId);
  }

  private configureImagesForUtility(utilityType: string): void {
    this.numberOfImages = 1;

    if (utilityType === 'ELEC_1P_KVA' || utilityType === 'ELEC_3P_KVA') {
      this.numberOfImages = 2;
    } else if (utilityType === 'ELEC_3P_3') {
      this.numberOfImages = 3;
    } else if (utilityType === 'ELEC_3P_TOU') {
      this.numberOfImages = 4;
    }

    this.images = [
      {
        base64: '',
        label:
          utilityType === 'ELEC_3P_1'
            ? 'Phase 1'
            : utilityType === 'ELEC_3P_TOU'
              ? 'Standard'
              : utilityType === 'WATER'
                ? 'Reading 1'
                : utilityType.includes('KVA') ||
                  utilityType === 'ELEC_3P_3' ||
                  utilityType === 'ELEC_1P'
                  ? 'Phase 1'
                  : 'Reading',
        placeholder: 'Capture Reading',
        formControl: 'captured',
      },
      {
        base64: '',
        label: utilityType.includes('KVA')
          ? 'KVA'
          : utilityType === 'ELEC_3P_TOU'
            ? 'Off-Peak'
            : 'Phase 2',
        placeholder: 'Capture Reading',
        formControl: 'captured2',
      },
      {
        base64: '',
        label: utilityType === 'ELEC_3P_TOU' ? 'Peak' : 'Phase 3',
        placeholder: 'Capture Reading',
        formControl: 'captured3',
      },
      {
        base64: '',
        label: 'KVA',
        placeholder: 'Capture KVA reading',
        formControl: 'captured4',
      },
    ];
  }

  async goBack(): Promise<void> {
    switch (this.source) {
      case 'scanner-a':
        this.router.navigateByUrl('/main/scanner-a');
        break;
      case 'scanner-b':
        this.router.navigateByUrl('/main/scanner-b');
        break;
      case 'scanner-c':
        this.router.navigateByUrl('/main/scanner-c');
        break;
      case 'scanner-d':
        this.router.navigateByUrl('/main/scanner-d');
        break;
      default:
        this.router.navigateByUrl('/main/meter-list');
        break;
    }
  }

  onSubmit(skipImages?: string): void {
    const skipValidate = skipImages || 'no';

    if (skipValidate === 'no' && !this.validateForm()) {
      return;
    }

    if (skipValidate !== 'no') {
      this.closeErrorMessage();
    }

    const formValues = this.imageForm.value;
    const limitedImages = this.images.slice(0, Math.min(this.numberOfImages, 4));

    const readings = limitedImages.map((image) => ({
      label: image.label,
      placeholder: image.placeholder,
      formControl: image.formControl,
      base64: image.base64,
      value: formValues[image.formControl] || '',
    }));

    const dialogRef = this.dialog.open(VerifyCaptureDialog, {
      data: { readings },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'submit') {
        this.submitCapture();
      }
    });
  }
  private isValidReadingPeriod(period: string): boolean {
    return this.workflow.isValidReadingPeriod(period);
  }

  private normalizeReadingPeriod(period: string | null | undefined): string {
    return this.workflow.normalizeReadingPeriod(period);
  }

  private submitCapture(): void {
    const formattedTimestamp = new Date().toISOString();
    const meterData = this.form.value;
    const currentUser = this.getCurrentUser();
    const currentProperty = this.getCurrentProperty();

    if (!currentUser || !currentProperty) {
      this.showError(
        'User session or property information is missing. Please log in again.'
      );
      return;
    }
    const safeReadingPeriod =
      this.normalizeReadingPeriod(this.datePeriod) ||
      this.normalizeReadingPeriod(localStorage.getItem('currentReadingPeriod')) ||
      this.resolveDatePeriod();
    const readingObject: MeterReading = {
      readingPeriod: safeReadingPeriod,
      meterId: meterData.id,
      meterNumber: this.form.value.meterNumber,
      propertyId: currentProperty,
      readingBy: currentUser.email,
      readingTS: formattedTimestamp,
      unitId: meterData.unitId,
      readingType: meterData.captureType,
      readingValueList: this.buildReadingValueList(),
      type: this.determineImageType(),
      image: this.buildImageArray(),
      fileName: `MeterCapture${formattedTimestamp}.png`,
      filename: `MeterCapture${formattedTimestamp}.png`,
      agentCode: currentUser.agentCode || '',
      sequence: '1',
    } as any;
    this.onCapture(readingObject);
  }

  private validateForm(): boolean {
    if (!this.form.valid || this.imageForm.value.captured === '') {
      this.showError('Please Capture a reading value.', 'Reading Value');
      return false;
    }

    const forceSubmit = localStorage.getItem('forceSubmit');

    if (forceSubmit !== 'true' && !this.hasValidImages()) {
      this.showError(
        'You have not captured any image/s. Do you want to continue without them?',
        'Image Warning'
      );
      return false;
    }

    return true;
  }
  private getCurrentUser(): any {
    return this.workflow.getCurrentUser();
  }

  private getCurrentProperty(): string {
    return this.workflow.getCurrentPropertyId(this.tenant?.propertyId || '');
  }

  private buildReadingValueList(): any[] {
    const readingValueList: any[] = [];

    ['captured', 'captured2', 'captured3', 'captured4'].forEach((field, index) => {
      const value = this.imageForm.value[field];
      if (value !== '' && value != null) {
        readingValueList.push({
          sequence: index + 1,
          value: Number(value),
        });
      }
    });

    return readingValueList;
  }

  private buildImageArray(): any[] {
    return this.images
      .filter((img) => !!img.base64 && String(img.base64).trim() !== '')
      .map((img) => ({
        image: img.base64,
      }));
  }

  private determineImageType(): string {
    const imageCount = this.images.filter((img) => img.base64).length;
    return imageCount === 1 ? 'oneImage' : 'multipleImages';
  }

  private hasValidImages(): boolean {
    return this.images.some(
      (img) => !!img.base64 && String(img.base64).trim().length > 50
    );
  }

  async onCapture(meter: MeterReading): Promise<void> {
    localStorage.removeItem('forceSubmit');

    this.isSaving = true;
    this.isUploadingImages = true;
    this.currentStep = 'Saving meter reading...';
    this.uploadProgress = 0;

    try {
      const result: SyncResult = await this.meterService.syncMeterReading(meter);

      if (result.success) {
        (meter as any).captured = 'CAPTURED';

        this.tenantService.updateTenant(meter, true);
        this.tenantService.updateTenantMeters(meter, true);

        const flag = result.captureResponse?.data?.captureMeterReadingSync?.flag;

        this.uploadProgress = 100;
        this.currentStep = 'Complete!';

        if (flag === 'OVER_FORECAST_CONSUMPTION') {
          this.checkFlag();
        } else {
          this.handleUploadComplete();
        }
        return;
      }

      throw new Error(result.error || 'Failed to sync reading');
    } catch (error: any) {
      console.error('Error capturing reading:', error);

      if (
        error?.statusText === 'Offline' ||
        error?.message?.toLowerCase().includes('offline')
      ) {
        (meter as any).captured = 'NOTSYNCED';

        await this.tenantService.updateMeter(meter);
        await this.tenantService.updateUnSyncedTenant(meter);
        await this.tenantService.createUnsyncedMeter(meter);

        this.showError(
          'Meter reading saved to device in offline mode. Please sync when back online',
          'Saved Offline'
        );
      } else {
        this.showError(this.getErrorMessage(error));
      }

      this.isSaving = false;
      this.isUploadingImages = false;
    }
  }

  checkFlag(): void {
    this.isSaving = false;
    this.isUploadingImages = false;
    this.uploadProgress = 100;
    this.currentStep = 'Complete!';
    this.showSuccessMessage = false;

    const formValues = this.imageForm.value;
    const limitedImages = this.images.slice(0, Math.min(this.numberOfImages, 4));

    const readings = limitedImages.map((image) => ({
      label: image.label,
      placeholder: image.placeholder,
      formControl: image.formControl,
      base64: image.base64,
      value: formValues[image.formControl] || '',
    }));

    const dialogRef = this.dialog.open(OverConsumtionDialog, {
      data: { readings },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === 'close') {
        this.handleUploadComplete();
      }
    });
  }

  private handleUploadComplete(): void {
    this.isSaving = false;
    this.isUploadingImages = false;
    this.uploadProgress = 100;
    this.currentStep = 'Complete!';
    this.showSuccessMessage = true;

    setTimeout(() => {
      if (this.showSuccessMessage) {
        this.closeSuccessMessage();
        this.goBack();
      }
    }, 2000);
  }

  private getErrorMessage(error: any): string {
    if (error?.message) return error.message;
    if (typeof error === 'string') return error;
    return 'An unexpected error occurred. Please try again.';
  }

  private showError(message: string, subMessage?: string): void {
    this.errorMessage = subMessage || '';
    this.subMessage = message;
    this.showErrorMessage = true;
  }

  async retryCapture(): Promise<void> {
    this.showErrorMessage = false;
    this.errorMessage = '';
    this.uploadProgress = 0;
    this.currentStep = '';
    localStorage.setItem('forceSubmit', 'true');
    this.onSubmit('no');
  }

  closeSuccessMessage(): void {
    this.showSuccessMessage = false;
    this.uploadProgress = 0;
    this.currentStep = '';
  }

  closeErrorMessage(): void {
    this.showErrorMessage = false;
    this.errorMessage = '';
    this.uploadProgress = 0;
    this.currentStep = '';
  }

  closeErrorMessageCancel(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.closeErrorMessage();
    this.goBack();
  }

  triggerImageCapture(fileInput: ElementRef<HTMLInputElement> | undefined): void {
    fileInput?.nativeElement.click();
  }

  handleImageCapture(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;

    if (input.files && input.files[0]) {
      const reader = new FileReader();
      reader.onload = () => {
        this.images[index].base64 = reader.result as string;
      };
      reader.readAsDataURL(input.files[0]);
    }
  }
  private async loadTargetDatesAndCheckCapture(): Promise<void> {
    const propertyId = this.tenant?.propertyId || this.storedMeter?.propertyId;

    if (!propertyId) {
      this.allowCapture = true;
      return;
    }

    try {
      const response = await this.propertyService.getTargetDate(propertyId);

      console.log('getTargetDate response:', response);

      if (!response?.isSuccessful || !response?.targetDate) {
        this.allowCapture = true;
        this.targetDates = null;
        return;
      }

      // keep reading period mainly for save/capture payload
      this.datePeriod =
        this.normalizeReadingPeriod(this.tenant?.readingPeriod) ||
        this.normalizeReadingPeriod(this.storedMeter?.readingPeriod) ||
        this.normalizeReadingPeriod(localStorage.getItem('currentReadingPeriod')) ||
        this.resolveDatePeriod();

      this.targetDates = response.targetDate;

      const current = new Date();
      const start = new Date(this.targetDates.startDate);
      const end = new Date(this.targetDates.endDate);

      console.log('Capture window check:', {
        current: current.toISOString(),
        start: start.toISOString(),
        end: end.toISOString(),
        allowCapture:
          current.getTime() >= start.getTime() &&
          current.getTime() <= end.getTime(),
        readingPeriod: this.datePeriod,
        meterId: this.tenant?.meterId || this.storedMeter?.meterId,
        propertyId,
      });

      this.allowCapture =
        current.getTime() >= start.getTime() &&
        current.getTime() <= end.getTime();
    } catch (error) {
      console.error('Failed to load target date:', error);
      this.allowCapture = true;
      this.targetDates = null;
    }
  }
  private resolveDatePeriod(): string {
    if (this.currentDate.getDate() < 20) {
      if (this.currentDate.getMonth() === 0) {
        return (this.currentDate.getFullYear() - 1).toString() + '12';
      }

      const monthNum = this.currentDate.getMonth();
      return (
        this.currentDate.getFullYear().toString() +
        (monthNum < 10 ? '0' + monthNum.toString() : monthNum.toString())
      );
    }

    return (
      this.currentDate.getFullYear().toString() +
      (this.currentDate.getMonth() < 9
        ? '0' + (this.currentDate.getMonth() + 1).toString()
        : (this.currentDate.getMonth() + 1).toString())
    );
  }

  private handleTenantNotFound(): void {
    console.log('Tenant not found');
  }
}